import { css } from '@emotion/css'
import type { EditorProps } from '@tiptap/pm/view'
import { useEditor, Tiptap, EditorEvents } from '@tiptap/react'
import debounce from 'lodash/debounce'
import merge from 'lodash/merge'
import { StrictMode, useEffect, useMemo, useCallback, FC } from 'react'
import { ThemeProvider, useTheme } from '@opallabs/hooks'
import { HyperlinkManager } from './components/HyperlinkManager'
import { TextEditorContextProvider } from './context'
import type { ExtensionOptions } from './extensions/definitions'
import { ListItemBackspace } from './extensions/ListItemBackspace'
import {
  useAssetStoreUpdate,
  useContentOverflow,
  useExtensions,
  customMergeExtensions,
  useInitialValue,
  useReadOnly,
  useSaveOnUnmount,
  useDocument,
} from './hooks'
import type { UseExtensionsReturnValue } from './hooks/useExtensions/types'
import * as styles from './styles'
import type { TextEditorProps, TextEditorAsset } from './types'
import { getContentChain } from './utils'

export { useTextEditor } from './context'
export type { TextEditorProps, TextEditorAsset }

const warnIfDisabled = <T extends ExtensionOptions>(
  enabled: UseExtensionsReturnValue<T>[1],
  extension: keyof T,
  conditions: unknown[]
) => {
  if (enabled[extension] !== true && conditions.some((c) => c != null)) {
    console.warn(
      `Props used by extension '${String(extension)}' were provided, but the extension isn't configured. These props will be ignored.`
    )
  }
}

export const TextEditor: FC<React.PropsWithChildren<TextEditorProps>> = ({
  children,
  className,
  contentStyle,
  focusControlBar,
  theme: propTheme,
  onSaveContent,
  onFocus,
  onBlur,
  onClick,
  onEscape,
  onEditorUpdate,
  autofocus = false,
  initialValue,
  readOnly = false,
  saveOnChange = false,
  saveOnUnmount = false,
  ariaLabel = '',
  dir = 'auto',
  characterLimit = null,
  placeholderText = '',
  overflowHeight = null,
  assetStore,
  onUploadAssets,
  onExpandAsset,
  extensions: consumerExtensions,
  editorPropsOverrides,
}) => {
  const providedTheme = useTheme()
  const theme = propTheme ?? providedTheme

  const { invalidateDocumentQuery } = useDocument() ?? {}

  const [extensions, enabled] = useExtensions(
    customMergeExtensions(
      { source: consumerExtensions, respectSource: true },
      {
        asset: { config: { onUploadAssets, onExpandAsset } },
        characterCount: { config: characterLimit ?? false },
        placeholder: { config: { placeholder: placeholderText } },
        listItemBackspace: { extension: ListItemBackspace },
      }
    )
  )

  // Print warnings if extension prop values are provided, but the
  // extension they're used by is disabled
  useEffect(() => {
    warnIfDisabled(enabled, 'asset', [onUploadAssets, onExpandAsset])
    warnIfDisabled(enabled, 'characterCount', [characterLimit])
    warnIfDisabled(enabled, 'placeholder', [placeholderText])
  }, [enabled, characterLimit, placeholderText, onUploadAssets, onExpandAsset])

  const debouncedSave = useMemo(() => {
    return debounce(
      ({ editor }: EditorEvents['update']) => {
        if (saveOnChange) {
          onSaveContent?.(getContentChain(editor))
        }

        /**
         * Refetching embeddedResource data is a bit awkward since TipTap is
         * communicating to the collaboration service via websockets and THAT
         * service updates rich-texts. Since we aren't going through React Query,
         * knowing when to invalidate and refetch from the server is a guessing
         * game. Here, we are waiting 2 seconds and then invalidating to give
         * time for the document source of truth to update. That should work in
         * most situations.
         */
        if (editor.storage.embeddedResource?.shouldRefetch) {
          editor.storage.embeddedResource.shouldRefetch = false
          setTimeout(() => invalidateDocumentQuery?.(), 2000)
        }
      },
      typeof saveOnChange !== 'boolean' ? saveOnChange.delay : 1000
    )
  }, [invalidateDocumentQuery, onSaveContent, saveOnChange])

  // Memoize focus/blur handlers
  // The `useEditor` hook doesn't like receiving undefined callbacks
  const handleFocus = useCallback(
    (props: EditorEvents['focus']) => onFocus?.(props),
    [onFocus]
  )
  const handleBlur = useCallback(
    (props: EditorEvents['blur']) => onBlur?.(props),
    [onBlur]
  )
  const handleDestroy = useCallback(
    () => debouncedSave.cancel(),
    [debouncedSave]
  )

  // Merge `editorProps` configuration for Tiptap
  const editorProps = useMemo(() => {
    return merge<EditorProps, EditorProps>(editorPropsOverrides ?? {}, {
      attributes: {
        'aria-label': ariaLabel,
        class: css([styles.editorContent({ theme }), contentStyle]),
        'data-testid': 'prosemirror-content',
        dir,
      },
      handleKeyDown: (view, e) => {
        if (e.altKey && e.key === 'F9') {
          focusControlBar?.()
        }
      },
    })
  }, [
    ariaLabel,
    editorPropsOverrides,
    dir,
    focusControlBar,
    theme,
    contentStyle,
  ])

  // Initialize the Tiptap editor
  const editor = useEditor(
    {
      autofocus,
      content: initialValue,
      editorProps,
      extensions,
      immediatelyRender: true,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onUpdate: debouncedSave,
      onDestroy: handleDestroy,
      shouldRerenderOnTransaction: false,
    },
    // Changes in this dependency list re-initialize the editor
    // When possible, use methods on the editor to update the
    // existing instance instead.
    // Event handlers (on*) do not need to be specified, the
    // hook handles their updates separately
    []
  )

  // Pass editor instance to parent's `onEditorUpdate` callback
  useEffect(() => {
    onEditorUpdate?.(editor)
    return () => onEditorUpdate?.(null)
  }, [onEditorUpdate, editor])

  // Apply any editorProps changes after init
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      editor.setOptions({ editorProps })
    }
  }, [editor, editorProps])

  const [isContentOverflowed] = useContentOverflow(editor, overflowHeight)
  useInitialValue(editor, initialValue)
  useReadOnly(editor, readOnly)
  useSaveOnUnmount(editor, saveOnUnmount, onSaveContent)
  useAssetStoreUpdate(editor, assetStore)

  return (
    <StrictMode>
      <ThemeProvider theme={theme}>
        <div css={styles.editorContainer} className={className}>
          {editor && (
            <TextEditorContextProvider
              editor={editor}
              saveContent={() => onSaveContent?.(getContentChain(editor))}
              uploadAssets={(enabled.asset && onUploadAssets) || undefined}
              isContentOverflowed={isContentOverflowed}
              onClick={onClick}
              onEscape={onEscape}
            >
              <Tiptap editor={editor}>
                {children}
                {enabled.link && <HyperlinkManager readOnly={readOnly} />}
              </Tiptap>
            </TextEditorContextProvider>
          )}
        </div>
      </ThemeProvider>
    </StrictMode>
  )
}
